import type { TreeOption } from "naive-ui";
import type { AssemblyMateDocumentModel, AssemblyMateProjectionDisplayContext, AssemblyMateProjectionParticipant } from "@puppycad/cad-core";
import {
	buildAssemblyMateTreeProjection,
	createAssemblyMateProjectionDisplayContextFromNodeMap,
	mergeAssemblyMateFoldersIntoTree,
} from "@/hooks/feature-tree/build-assembly-mate-tree-projection";
import { FeatureTreeNodeKind } from "@/hooks/feature-tree/feature-tree-types";
import type { FeatureTreeNodeMeta } from "@/hooks/feature-tree/feature-tree-types";

/**
 * 将配合文档变化限制在已有模型树的配合文件夹内。
 *
 * 结构发布时缓存可挂载节点的骨架，保留原后序归属规则；热态不再读取实体分支、Worker 或 GLB provider。
 * 命名和归属仍调用完整投影使用的 SDK/纯函数，不另建稳定 ID 解析或业务状态。
 */
export class AssemblyMateTreeProjectionIndex {
	/** 当前纯数据结构版本，以数组与元数据身份为边界；完整发布会清理。 */
	private tree: readonly TreeOption[] | null = null;
	private nodeMap: Map<string, FeatureTreeNodeMeta> | null = null;
	/** 只含组件挂载点，保留装配祖先与后序遍历关系的模型骨架。 */
	private skeleton: TreeOption[] = [];
	/** 仅在当前模型结构与名称版本内有效的 SDK 显示上下文。 */
	private displayContext: AssemblyMateProjectionDisplayContext | null = null;
	/** 真实 UI 节点及稳定非配合子列表，不拥有这些对象的生命周期。 */
	private readonly hosts = new Map<string, AssemblyMateTreeHostBinding>();
	/** 归属解析需要的组件摘要，不包含实体/草图等无关元数据。 */
	private readonly modelMetadata = new Map<string, FeatureTreeNodeMeta>();
	/** 本投影拥有的配合元数据 key，供下次发布清理失效项。 */
	private readonly mateKeys = new Set<string>();
	/** 显式观测累计计数，clear 仅释放当前引用。 */
	private builds = 0;
	private changes = 0;

	/**
	 * 更新配合定义投影，不修改非配合模型节点的显示字段或身份。
	 * @param tree 当前 UI 树；完整结构发布后必须传入新的树或先 clear。
	 * @param nodeMap 当前交互元数据，新增和删除与树节点同步写回。
	 * @param document 最新 SDK 文档，包含全量 Mate 定义以维持命名序号及引用一致。
	 * @returns 是否实际更新了树展示或结构。
	 */
	public apply(tree: readonly TreeOption[], nodeMap: Map<string, FeatureTreeNodeMeta>, document: AssemblyMateDocumentModel): boolean {
		if (this.tree !== tree || this.nodeMap !== nodeMap) this.bind(tree, nodeMap);
		const previousChanges = this.changes;
		const projection = buildAssemblyMateTreeProjection(document, undefined, this.displayContext ?? undefined);
		const nextMetadata = new Map(this.modelMetadata);
		projection.featureTreeNodeMap.forEach((meta, key) => nextMetadata.set(key, meta));
		// 仅在挂载骨架上执行既有规则；透明模型容器被折叠但节点顺序、装配祖先关系保持不变。
		const projectedSkeleton = mergeAssemblyMateFoldersIntoTree(this.skeleton, projection, nextMetadata);
		const visit = (node: TreeOption): void => {
			const key = String(node.key);
			const binding = this.hosts.get(key);
			if (!binding) return;
			const children = node.children ?? [];
			const folders = children.filter(child => child.type === "assembly-mate-folder");
			const nextFolders = this.synchronizeChildren(binding.folders, folders);
			if (nextFolders !== binding.folders) {
				binding.node.children = [...binding.modelChildren, ...nextFolders];
				binding.node.isLeaf = binding.node.children.length === 0;
				// 写入响应式树后再读取，禁止下次对未代理的新节点写字段导致 UI 不更新。
				binding.folders = binding.node.children.filter(child => child.type === "assembly-mate-folder");
				this.changes++;
			}
			for (const child of children) if (child.type !== "assembly-mate-folder") visit(child);
		};
		projectedSkeleton.forEach(visit);
		// 清理仅由本投影拥有的旧配合元数据，不扫描或覆盖模型体的身份绑定。
		for (const key of this.mateKeys) nodeMap.delete(key);
		this.mateKeys.clear();
		for (const [key, metadata] of nextMetadata) {
			if (metadata.kind !== FeatureTreeNodeKind.AssemblyMate && metadata.kind !== FeatureTreeNodeKind.AssemblyMateFolder) continue;
			nodeMap.set(key, metadata);
			this.mateKeys.add(key);
		}
		return this.changes !== previousChanges;
	}

	/** @returns 显式观测统计，不在业务循环创建统计快照。 */
	public getStats(): AssemblyMateTreeProjectionStats {
		return { builds: this.builds, hosts: this.hosts.size, changes: this.changes };
	}

	/** 文档卸载或完整投影发布时释放所有树、名称和节点引用，不销毁宿主数据。 */
	public clear(): void {
		this.tree = null;
		this.nodeMap = null;
		this.skeleton = [];
		this.displayContext = null;
		this.hosts.clear();
		this.modelMetadata.clear();
		this.mateKeys.clear();
	}

	/** @param tree 新结构树。 @param nodeMap 与结构配对的元数据。 */
	private bind(tree: readonly TreeOption[], nodeMap: Map<string, FeatureTreeNodeMeta>): void {
		this.clear();
		this.tree = tree;
		this.nodeMap = nodeMap;
		this.displayContext = createAssemblyMateProjectionDisplayContextFromNodeMap(nodeMap);
		for (const [key, metadata] of nodeMap) {
			if (metadata.kind === FeatureTreeNodeKind.AssemblyMate || metadata.kind === FeatureTreeNodeKind.AssemblyMateFolder) this.mateKeys.add(key);
		}
		const visit = (node: TreeOption): TreeOption[] => {
			if (node.type === "assembly-mate-folder") return [];
			const children = node.children ?? [];
			const descendants = children.flatMap(visit);
			const key = String(node.key);
			const metadata = nodeMap.get(key);
			if (metadata?.kind !== FeatureTreeNodeKind.GlbNode || (metadata.glbNodeRole !== "assembly" && metadata.glbNodeRole !== "part")) return descendants;
			this.modelMetadata.set(key, metadata);
			this.hosts.set(key, {
				node,
				modelChildren: children.filter(child => child.type !== "assembly-mate-folder"),
				folders: children.filter(child => child.type === "assembly-mate-folder"),
			});
			return [{ key: node.key, children: descendants }];
		};
		this.skeleton = tree.flatMap(visit);
		this.builds++;
	}

	/** @param previous 当前配合节点列表。 @param next 权威投影。 @returns 保留稳定节点引用的子列表。 */
	private synchronizeChildren(previous: readonly TreeOption[], next: readonly TreeOption[]): readonly TreeOption[] {
		const previousByKey = new Map(previous.map(node => [node.key, node]));
		const reconciled = next.map(node => {
			const current = previousByKey.get(node.key);
			if (!current) {
				this.changes++;
				return node;
			}
			this.synchronizeNode(current, node);
			return current;
		});
		return previous.length === reconciled.length && previous.every((node, index) => node === reconciled[index]) ? previous : reconciled;
	}

	/** @param current 实际响应式配合节点。 @param next 新投影，所有非 children 字段均为标量或参与者摘要。 */
	private synchronizeNode(current: TreeOption, next: TreeOption): void {
		let changed = false;
		for (const key of Object.keys(current)) {
			if (key !== "children" && !(key in next)) {
				delete current[key];
				changed = true;
			}
		}
		for (const key of Object.keys(next)) {
			if (key === "children") continue;
			const same =
				key === "mateParticipants"
					? this.areParticipantsEqual(
							current[key] as readonly AssemblyMateProjectionParticipant[] | undefined,
							next[key] as readonly AssemblyMateProjectionParticipant[] | undefined
						)
					: Object.is(current[key], next[key]);
			if (!same) {
				current[key] = next[key];
				changed = true;
			}
		}
		const children = this.synchronizeChildren(current.children ?? [], next.children ?? []);
		if (children !== current.children) {
			current.children = [...children];
			changed = true;
		}
		if (changed) this.changes++;
	}

	/** @param previous 当前参与者摘要。 @param next 新摘要。 @returns 稳定引用及显示语义是否完全相同。 */
	private areParticipantsEqual(
		previous: readonly AssemblyMateProjectionParticipant[] | undefined,
		next: readonly AssemblyMateProjectionParticipant[] | undefined
	): boolean {
		if (previous === next) return true;
		if (!previous || !next || previous.length !== next.length) return false;
		return previous.every((item, index) => {
			const candidate = next[index];
			return (
				item.stableRef === candidate.stableRef &&
				item.assemblyNodeId === candidate.assemblyNodeId &&
				item.componentInstanceId === candidate.componentInstanceId &&
				item.componentLabel === candidate.componentLabel &&
				item.entityLabel === candidate.entityLabel &&
				item.entityKind === candidate.entityKind
			);
		});
	}
}
